import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { cn } from "@/lib/utils";
import { Button, Dialog, DialogTitle, IconV2, Typography } from "@bookips/sds";
import { useEffect } from "react";
import PassInfoDialogBody from "../PassInfoDialog/PassInfoDialogBody";
/**
 * 구독은 plan·구독 응답에 guideButtonUrl·category 가 없어 이용방법 안내는 기본 URL 로 고정한다.
 * (PassInfoDialogFooter 는 pass.guideButtonUrl → category 순으로 고르지만 구독엔 둘 다 없다)
 */ const DEFAULT_GUIDE_URL = "https://solvookguide.oopy.io/waytouse";
/**
 * 월구독 이용권/구독 정보 다이얼로그 — 결제 페이지 '이용권 정보 보기' 와
 * 마이쏠북 구독 관리 '상세정보' 가 공유하는 껍데기.
 *
 * 본문은 PassInfoDialogBody 를 그대로 재사용해 기존 이용권 다이얼로그와 같은 모양이 된다.
 * 섹션 분기는 호출처의 파생 함수(getSubscriptionBenefitSections / getSubscriptionInfoSections)에만 있다.
 *
 * PassInfoDialog 를 쓰지 않는 이유는 그쪽이 UseTicketProductInfo(필수 필드 17개)를 요구하는데
 * 구독 응답에는 사용범위 5개 필드만 있어서다. 없는 필드를 지어내면
 * getPassInfoSections 의 category·deductionMethod 분기가 엉뚱한 섹션을 만든다.
 *
 * ⚠️ Dialog 껍데기(모바일 bottom-sheet, svh, transform reset)는 PassInfoDialog/index.tsx 와
 * 같은 값이다. 5개 화면이 쓰는 PassInfoDialog 까지 묶는 공용화와 모바일 풀페이지 대응은
 * 회귀 리뷰 범위가 달라 별도 작업으로 분리했다 (PassInfoDialog/plan.md §5-1).
 */ const SubscriptionInfoDialog = (param)=>{
    let { title , sections , onClose , tracking  } = param;
    // 다이얼로그 노출 시 1회 발사. mount 시점 고정이라 후속 prop 변경으로 재발사되지 않는다
    useEffect(()=>{
        if (!tracking) return;
        mixpanelEvent("Popup Viewed", {
            Position: tracking.position,
            "Popup Type": "이용권 상세 다이얼로그",
            "Popup Title": title,
            "Membership Id": tracking.subscriptionId
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const trackButton = (buttonName, buttonUrl)=>{
        if (!tracking) return;
        mixpanelEvent("Popup Button Clicked", {
            Position: tracking.position,
            "Popup Type": "이용권 상세 다이얼로그",
            "Popup Title": title,
            "Button Name": buttonName,
            "Button URL": buttonUrl,
            "Membership Id": tracking.subscriptionId
        });
    };
    const handleUsageGuide = ()=>{
        trackButton("이용방법 보기", DEFAULT_GUIDE_URL);
        window.open(DEFAULT_GUIDE_URL, "_blank", "noopener,noreferrer");
    };
    const handleConfirm = ()=>{
        trackButton("확인");
        onClose();
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: true,
        onOpenChange: (isOpen)=>!isOpen && onClose(),
        scroll: true,
        classes: {
            contentWrapper: cn("w-[520px] max-w-[calc(100vw-40px)] max-h-[calc(100vh-144px)] gap-2", // 모바일 bottom-sheet 모양 (SDS BottomSheet 부재 — Tailwind override 로 처리)
            // !translate-* 는 SDS Dialog 의 기본 중앙 정렬 transform 을 reset 하기 위한 것.
            // iOS Chrome 하단 탭바에 가려지지 않도록 svh 사용 (vh 는 chrome 영역 포함이라 잘림 발생)
            "max-sm:!w-full max-sm:!max-w-full max-sm:!max-h-[calc(100svh-48px)]", "max-sm:!fixed max-sm:!bottom-0 max-sm:!left-0 max-sm:!top-auto", "max-sm:!translate-x-0 max-sm:!translate-y-0", "max-sm:!rounded-b-none max-sm:!rounded-t-2xl", "max-sm:data-[state=open]:animate-slide-in-from-bottom", "max-sm:data-[state=closed]:animate-slide-out-to-bottom"),
            content: "flex flex-col min-h-0 pb-7"
        },
        title: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-start justify-between gap-2 px-5 pt-7 sm:hidden",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "h6",
                            className: "text-primary flex-1 break-words",
                            children: title
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            "aria-label": "닫기",
                            onClick: onClose,
                            className: "mt-0.5 shrink-0",
                            children: /*#__PURE__*/ _jsx(IconV2.Close, {
                                size: "sm"
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "hidden items-start justify-between gap-2 px-7 pt-7 sm:flex",
                    children: [
                        /*#__PURE__*/ _jsx(DialogTitle, {
                            title: title,
                            className: "flex-1 break-all"
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            "aria-label": "닫기",
                            onClick: onClose,
                            className: "mt-1 block size-6 shrink-0 cursor-pointer focus:outline-none",
                            children: /*#__PURE__*/ _jsx(IconV2.Close, {
                                size: "sm"
                            })
                        })
                    ]
                })
            ]
        }),
        footer: /*#__PURE__*/ _jsxs("div", {
            className: "flex gap-2 px-7 pt-5 pb-5 max-sm:px-5 max-sm:pt-4 max-sm:pb-4 [&>button]:w-full",
            children: [
                /*#__PURE__*/ _jsx(Button, {
                    variant: "stroke",
                    color: "secondary",
                    size: "lg",
                    onClick: handleUsageGuide,
                    children: "이용방법 보기"
                }),
                /*#__PURE__*/ _jsx(Button, {
                    variant: "fill",
                    color: "primary",
                    size: "lg",
                    onClick: handleConfirm,
                    children: "확인"
                })
            ]
        }),
        children: /*#__PURE__*/ _jsx(PassInfoDialogBody, {
            sections: sections
        })
    });
};
export default SubscriptionInfoDialog;
